/**
 * PERFORMANCE OPTIMIZATION: Lazy Load Growave Reviews
 *
 * Instead of loading Growave immediately (blocks ~600-800ms), we:
 * 1. Wait for user interaction (scroll, click, or timeout)
 * 2. Use Intersection Observer to detect when reviews section is near viewport
 * 3. Load Growave only when needed
 *
 * Expected TBT Reduction: ~600-800ms on product pages
 */

(function() {
  'use strict';

  // Exit early if not on product page or Growave already loaded
  if (!window.shopifyAppConfig?.loadGrowave || window.gw) {
    return;
  }

  let loaded = false;
  let observer = null;

  /**
   * Load Growave scripts and initialize
   */
  function loadGrowave() {
    if (loaded || window.gw) return;
    loaded = true;

    // Disconnect observer if it exists
    if (observer) {
      observer.disconnect();
      observer = null;
    }

    // Remove event listeners
    document.removeEventListener('scroll', scheduleLoad);
    document.removeEventListener('click', scheduleLoad);
    document.removeEventListener('touchstart', scheduleLoad);

    // Create Growave script
    const script = document.createElement('script');
    script.src = 'https://cdn.growave.io/api/store/firstchoicemarine.myshopify.com/loader.js';
    script.async = true;
    script.setAttribute('data-growave-loader', '');

    // Add to head
    document.head.appendChild(script);

    console.log('[Performance] Growave loaded lazily');
  }

  /**
   * Schedule loading after a small delay to avoid blocking current task
   */
  function scheduleLoad() {
    // Use requestIdleCallback if available, otherwise setTimeout
    if ('requestIdleCallback' in window) {
      requestIdleCallback(loadGrowave, { timeout: 2000 });
    } else {
      setTimeout(loadGrowave, 100);
    }
  }

  /**
   * Set up Intersection Observer for reviews section
   */
  function setupIntersectionObserver() {
    // Find Growave review container (common selectors)
    const reviewsSelectors = [
      '#shopify-product-reviews',
      '[data-growave-reviews]',
      '.gw-reviews',
      '.spr-container',
      '#reviews'
    ];

    let reviewsElement = null;
    for (const selector of reviewsSelectors) {
      reviewsElement = document.querySelector(selector);
      if (reviewsElement) break;
    }

    // If no reviews element found, load after delay anyway
    if (!reviewsElement) {
      setTimeout(loadGrowave, 8000); // 8 seconds fallback
      return;
    }

    // Create Intersection Observer
    observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          // Load when reviews section is within 300px of viewport
          if (entry.isIntersecting || entry.intersectionRatio > 0) {
            scheduleLoad();
          }
        });
      },
      {
        rootMargin: '300px 0px', // Load 300px before visible
        threshold: 0
      }
    );

    observer.observe(reviewsElement);
  }

  /**
   * Initialize lazy loading strategy
   */
  function init() {
    // Strategy 1: Load on first user interaction
    const interactionEvents = ['scroll', 'click', 'touchstart'];
    interactionEvents.forEach(event => {
      document.addEventListener(event, scheduleLoad, { once: true, passive: true });
    });

    // Strategy 2: Use Intersection Observer for reviews section
    if ('IntersectionObserver' in window) {
      // Wait for DOM to be ready
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', setupIntersectionObserver);
      } else {
        setupIntersectionObserver();
      }
    } else {
      // Fallback for browsers without IntersectionObserver
      setTimeout(loadGrowave, 5000);
    }

    // Strategy 3: Timeout fallback (load after 10 seconds regardless)
    setTimeout(loadGrowave, 10000);
  }

  // Start lazy loading
  init();
})();
